import { useState, useEffect } from 'react';
import { loadAuditData, getSiteById } from '@/lib/data';
import type { AuditResults, SiteAudit } from '@/types/audit';

interface UseAuditDataResult {
  data: AuditResults | null;
  loading: boolean;
  error: Error | null;
}

export function useAuditData(): UseAuditDataResult {
  const [data, setData] = useState<AuditResults | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let mounted = true;

    async function fetchData() {
      try {
        const result = await loadAuditData();
        if (mounted) {
          setData(result);
          setLoading(false);
        }
      } catch (err) {
        if (mounted) {
          setError(err as Error);
          setLoading(false);
        }
      }
    }

    fetchData();

    return () => {
      mounted = false;
    };
  }, []);

  return { data, loading, error };
}

interface UseSiteDataResult {
  site: SiteAudit | null;
  loading: boolean;
  error: Error | null;
}

export function useSiteData(siteId: string | undefined): UseSiteDataResult {
  const [site, setSite] = useState<SiteAudit | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let mounted = true;

    async function fetchData() {
      if (!siteId) {
        setLoading(false);
        return;
      }

      try {
        const result = await getSiteById(siteId);
        if (mounted) {
          setSite(result || null);
          setLoading(false);
        }
      } catch (err) {
        if (mounted) {
          setError(err as Error);
          setLoading(false);
        }
      }
    }

    fetchData();

    return () => {
      mounted = false;
    };
  }, [siteId]);

  return { site, loading, error };
}
